/**
 * Reach HUD text (plan R15): every string the HUD shows that depends on the galaxy, the layout or the state. These
 * run only when what they describe changes (a new nav target, a new structure list, an event), never per frame, so
 * they may allocate. The per-frame model caches their results and compares cheap keys instead (D-014).
 */
import type { FactionKey, LaneEnd, SystemLayout } from '../../galaxy/layout/types'
import type { Galaxy, PoiKind, RegionId } from '../../galaxy/types'
import { REV } from '../../reach/events'
import type { ActiveStructure, InteractionKind, MassLockReason } from '../../reach/runtime/types'
import reactivationText from '../../story-link/reactivation/text.json'
import registrantText from '../../story-link/reactivation/registrant.json'
import hulksJson from '../../data/hulks.json'
import { navPointOf } from '../../reach/navPoints'
import type { NavTargetRef, PartId, ReachState } from '../../reach/types'
import type { HudLabelMode, ReachHudConfig } from './config'
import { formatCredits } from './format'

/** Display names the HUD needs from the data files, resolved once at init. */
export interface HudNames {
  /** Region labels (galaxy.json `regions[].label`). */
  readonly regions: Readonly<Record<RegionId, string>>
  /** Faction short names (factions.json `short`). */
  readonly factions: Readonly<Record<FactionKey, string>>
}

export const POI_LABELS: Readonly<Record<PoiKind, string>> = Object.freeze({
  derelict: 'Derelict',
  cache: 'Cache',
  anomaly: 'Anomaly',
  'hidden-base': 'Hidden base',
  'nav-beacon': 'Nav beacon',
})

export const PART_LABELS: Readonly<Record<PartId, string>> = Object.freeze({
  hull_plating: 'Hull plating',
  electronics: 'Electronics',
  drive_parts: 'Drive parts',
})

/** The cruise panel's mass-lock wording (plan R15: station / relay / planet / weapon lock). */
export const MASS_LOCK_LABELS: Readonly<Record<MassLockReason, string>> = Object.freeze({
  none: '',
  station: 'MASS-LOCKED: STATION',
  relay: 'MASS-LOCKED: RELAY',
  body: 'MASS-LOCKED: PLANET',
  ship: 'MASS-LOCKED: LARGE SHIP',
  weaponLock: 'MASS-LOCKED: WEAPON LOCK',
})

/** What clears each mass-lock, shown under the lock on the cruise panel. */
export const MASS_LOCK_HINTS: Readonly<Record<MassLockReason, string>> = Object.freeze({
  none: '',
  station: 'FLY CLEAR OF THE STATION TO CRUISE',
  relay: 'FLY CLEAR OF THE RELAY TO CRUISE',
  body: 'CLIMB AWAY FROM THE PLANET TO CRUISE',
  ship: 'FLY CLEAR OF THE LARGE SHIP TO CRUISE',
  weaponLock: 'BREAK THE WEAPON LOCK TO CRUISE',
})

/** The interaction prompt's action wording (hail adds the station name). */
const PROMPT_VERBS: Readonly<Record<InteractionKind, string>> = Object.freeze({
  none: '',
  hail: 'Hail',
  deliverParts: 'Deliver parts',
  beginReboot: 'Begin reboot',
  repairLaneRing: 'Repair lane ring',
  openCache: 'Open cache',
  readBeacon: 'Read beacon',
  reactVent: reactivationText.prompts.vent,
  reactFence: reactivationText.prompts.fence,
  reactHail: reactivationText.prompts.hail,
  reactDeliver: reactivationText.prompts.deliver,
  reactRestart: reactivationText.prompts.restart,
  reactFight: reactivationText.prompts.fight,
  reactRegister: registrantText.prompt,
  reactCandle: registrantText.harrow.candlePrompt,
  hulkVent: hulksJson.text.prompts.vent,
  hulkFence: hulksJson.text.prompts.fence,
  hulkFight: hulksJson.text.prompts.fight,
  hulkStart: hulksJson.text.prompts.start,
  hulkDeliver: hulksJson.text.prompts.deliver,
  hulkClaim: hulksJson.text.prompts.claim,
})

const UNKNOWN_SYSTEM = 'Unknown system'

/** A system's name as the player knows it: the name once rumoured or charted, "Unknown system" before. */
export function knownSystemName(galaxy: Galaxy, state: ReachState, systemId: string): string {
  if (state.knowledge[systemId] === undefined) return UNKNOWN_SYSTEM
  const sys = galaxy.systems.find((s) => s.id === systemId)
  return sys ? sys.name : UNKNOWN_SYSTEM
}

/** The system on the other side of relay `relayIndex` (galaxy index) from `fromSystemId`, or null. */
export function relayOtherSystem(galaxy: Galaxy, relayIndex: number, fromSystemId: string): string | null {
  const relay = galaxy.relays[relayIndex]
  if (!relay) return null
  if (relay.a === fromSystemId) return relay.b
  if (relay.b === fromSystemId) return relay.a
  return null
}

function laneEndName(layout: SystemLayout, end: LaneEnd): string {
  if (end.kind === 'station') {
    const st = layout.stations.find((s) => s.id === end.id)
    if (st) return st.name
  }
  return bodyName(layout, end.id)
}

/** A body's display name: "⟨system⟩ ⟨n⟩" (the generator gives bodies ids, not names). */
export function bodyName(layout: SystemLayout, bodyId: string): string {
  const b = layout.bodies.find((x) => x.id === bodyId)
  return b ? `${layout.name} ${b.index + 1}` : bodyId
}

/** "LANE Harbour Hub → Tethys Refinery". */
export function laneText(layout: SystemLayout, laneIndex: number): string {
  const lane = layout.lanes[laneIndex]
  if (!lane) return 'LANE'
  return `LANE ${laneEndName(layout, lane.from)} → ${laneEndName(layout, lane.to)}`
}

/** "ROUTE ▸ ⟨next system⟩". */
export function routeText(nextSystemName: string): string {
  return `ROUTE ▸ ${nextSystemName}`
}

/** The name of a nav target in the current layout; the reference's id when the layout does not hold it. */
export function navTargetName(layout: SystemLayout, galaxy: Galaxy, state: ReachState, ref: NavTargetRef): string {
  switch (ref.kind) {
    case 'station': {
      const st = layout.stations.find((s) => s.id === ref.id)
      return st ? st.name : ref.id
    }
    case 'relay': {
      const r = layout.relays.find((x) => x.relayId === ref.id)
      return r ? `Relay ▸ ${knownSystemName(galaxy, state, r.otherSystemId)}` : ref.id
    }
    case 'poi': {
      const p = layout.pois.find((x) => x.id === ref.id)
      return p ? POI_LABELS[p.kind] : ref.id
    }
    case 'body':
      return bodyName(layout, ref.id)
    case 'field': {
      const f = layout.fields.find((x) => x.id === ref.id)
      return f ? `${f.kind === 'ice' ? 'Ice' : f.kind === 'gas' ? 'Gas' : 'Asteroid'} field` : ref.id
    }
    case 'laneRing':
      return 'Lane ring'
    case 'hulk':
      return navPointOf(layout.systemId, ref.id)?.name ?? 'Hulk'
  }
}

/** The float64 position of a nav target in the layout, into `out`; false when the layout does not hold it. */
export function navTargetPosition(layout: SystemLayout, ref: NavTargetRef, out: { x: number; y: number; z: number }): boolean {
  let p: readonly [number, number, number] | null
  if (ref.kind === 'station') p = layout.stations.find((s) => s.id === ref.id)?.position ?? null
  else if (ref.kind === 'relay') p = layout.relays.find((s) => s.relayId === ref.id)?.position ?? null
  else if (ref.kind === 'poi') p = layout.pois.find((s) => s.id === ref.id)?.position ?? null
  else if (ref.kind === 'body') p = layout.bodies.find((s) => s.id === ref.id)?.position ?? null
  else if (ref.kind === 'field') p = layout.fields.find((s) => s.id === ref.id)?.center ?? null
  else if (ref.kind === 'hulk') p = navPointOf(layout.systemId, ref.id)?.position ?? null
  else p = layout.laneRings.find((s) => s.id === ref.id)?.position ?? null
  if (p === null) return false
  out.x = p[0]
  out.y = p[1]
  out.z = p[2]
  return true
}

/**
 * A nearby structure's label, or null when it gets none (a hidden POI the player has not discovered). Relays name
 * their destination and state; down lane rings say so; looted POIs say so.
 */
export function structureLabel(layout: SystemLayout, galaxy: Galaxy, state: ReachState, s: ActiveStructure): string | null {
  switch (s.kind) {
    case 'station': {
      const st = layout.stations.find((x) => x.id === s.id)
      return st ? st.name : s.id
    }
    case 'relay': {
      const r = layout.relays.find((x) => x.relayId === s.id)
      const dest = r ? knownSystemName(galaxy, state, r.otherSystemId) : UNKNOWN_SYSTEM
      const suffix = s.state === 0 ? ' · OFFLINE' : s.state === 2 ? ' · REBOOT' : ''
      return `Relay ▸ ${dest}${suffix}`
    }
    case 'laneRing':
      return s.state === 0 ? 'Lane ring · DOWN' : 'Lane ring'
    case 'poi': {
      const p = layout.pois.find((x) => x.id === s.id)
      if (!p) return null
      if (p.hidden && !(state.discoveredPois[layout.systemId]?.includes(p.id) ?? false)) return null
      return s.state === 1 ? `${POI_LABELS[p.kind]} · EMPTY` : POI_LABELS[p.kind]
    }
  }
}

/** The prompt's action text: "Hail Harbour Hub", "Deliver parts", … */
export function promptText(layout: SystemLayout | null, kind: InteractionKind, index: number): string {
  if (kind === 'hail') {
    const st = layout?.stations.find((s) => s.index === index)
    return st ? `Hail ${st.name}` : 'Hail'
  }
  return PROMPT_VERBS[kind]
}

/** The keyboard glyph of a key code: "KeyF" → "F", "Digit4" → "4", "Enter" → "Enter". */
export function keyGlyph(code: string): string {
  if (code.startsWith('Key') && code.length === 4) return code.slice(3)
  if (code.startsWith('Digit') && code.length === 6) return code.slice(5)
  return code
}

/** The system banner: name and "⟨region⟩ · ⟨owner⟩". */
/** The banner's owner words (reach-hud.json `banner.text`, CL-7). */
export type BannerWords = ReachHudConfig['banner']['text']

/** Whether `systemId`'s Charter claim is held (true), unheld (false), or not known yet (null). */
export type BannerClaimSource = (systemId: string) => boolean | null

let bannerClaims: BannerClaimSource | null = null

/** Binds (or, with null, unbinds) where the banner reads whether a system's claim is held (the Charter HUD, CL-7). */
export function setBannerClaimSource(fn: BannerClaimSource | null): void {
  bannerClaims = fn
}

/** The bound claim source's answer for `systemId` (null unbound or unknown). Event rate (a system entry). */
export function bannerClaimed(systemId: string): boolean | null {
  return bannerClaims === null ? null : bannerClaims(systemId)
}

/**
 * The system banner: the name, and the region with the layout owner ("Gate Cluster · Authority space"). CL-7 (D-1158
 * Q3): when the Charter says no one holds the claim (`claimed` false), "· unclaimed" follows ("Gate Cluster ·
 * Authority space · unclaimed"); not known yet (null), nothing does. Wording only (the D-160 rule stays).
 */
export function bannerText(
  galaxy: Galaxy,
  layout: SystemLayout | null,
  names: HudNames,
  systemIndex: number,
  words: BannerWords,
  claimed: boolean | null = null,
): { name: string; sub: string } {
  const sys = galaxy.systems[systemIndex]
  if (!sys) return { name: '', sub: '' }
  const owner = layout?.owner ?? null
  const region = names.regions[sys.region]
  if (owner === null) return { name: sys.name.toUpperCase(), sub: claimed === true ? region : `${region} · ${words.unowned}` }
  const ownerText = words.owned.replace('{faction}', names.factions[owner])
  const sub = claimed === false ? `${region} · ${ownerText} · ${words.unclaimed}` : `${region} · ${ownerText}`
  return { name: sys.name.toUpperCase(), sub }
}

/** Toast tone: styles the toast (and never by colour alone: bad toasts also carry a marker glyph). */
export type ToastTone = 'info' | 'good' | 'warn' | 'bad'

/** What a toast needs to word itself. */
export interface ToastWords {
  readonly galaxy: Galaxy
  layoutOfIndex(systemIndex: number): SystemLayout | null
}

/** The tone of a toastable reach event. */
export function toastTone(type: number, b: number): ToastTone {
  if (type === REV.saveFailed) return 'bad'
  if (type === REV.transitEnd) return b === 1 ? 'bad' : 'info'
  if (type === REV.rebootEnd) return b === 0 ? 'good' : b === 1 ? 'bad' : 'warn'
  if (type === REV.chartValue || type === REV.chartsSold || type === REV.cacheOpened || type === REV.laneRingRepaired) return 'good'
  return 'info'
}

function systemNameAt(galaxy: Galaxy, index: number): string {
  return galaxy.systems[index]?.name ?? UNKNOWN_SYSTEM
}

/** A toast's text for reach event `type` with fields a, b and amount; null when the event is not toasted. */
export function toastText(words: ToastWords, type: number, a: number, b: number, amount: number): string | null {
  const g = words.galaxy
  switch (type) {
    case REV.systemCharted:
      return `Charted: ${systemNameAt(g, a)}`
    case REV.systemRumoured:
      return `Rumoured: ${systemNameAt(g, a)}`
    case REV.poiRevealed: {
      const poi = words.layoutOfIndex(a)?.pois[b]
      return `Point of interest: ${poi ? POI_LABELS[poi.kind] : 'signal'}`
    }
    case REV.chartValue:
      return `Chart data +${formatCredits(amount)}`
    case REV.chartsSold:
      return `Charts sold +${formatCredits(amount)}`
    case REV.partsDelivered:
      return `Parts delivered: ${Math.round(amount)}`
    case REV.relayScanned:
      return 'Relay scanned: repair list on the HUD'
    case REV.rebootEnd:
      return b === 0 ? 'Relay online' : b === 1 ? 'Relay destroyed. Parts lost.' : 'Reboot aborted'
    case REV.cacheOpened:
      return amount > 0 ? `Cache opened +${formatCredits(amount)}` : 'Cache opened'
    case REV.beaconRead:
      return 'Beacon read: new rumours'
    case REV.laneRingRepaired:
      return 'Lane ring repaired'
    case REV.transitEnd:
      // b = 1: the destination switch failed and the ship came back out of the same relay (R17)
      return b === 1 ? 'Relay jump failed' : null
    case REV.saved:
      return b === 1 ? 'Game saved' : 'Autosaved'
    case REV.saveFailed:
      return 'Save failed'
    default:
      return null
  }
}

/** Reach event codes that make a toast (a lookup table for the per-frame event scan). */
export const TOAST_EVENTS: readonly number[] = [
  REV.systemCharted,
  REV.systemRumoured,
  REV.poiRevealed,
  REV.chartValue,
  REV.chartsSold,
  REV.partsDelivered,
  REV.relayScanned,
  REV.rebootEnd,
  REV.cacheOpened,
  REV.beaconRead,
  REV.laneRingRepaired,
  REV.transitEnd,
  REV.saved,
  REV.saveFailed,
]

// ---- Label declutter (spec 2026-10-09 H2) ----------------------------------------------------------------------

/** What a nearby label shows after the declutter. */
export const LABEL_HIDDEN = 0
export const LABEL_ICON = 1
export const LABEL_FULL = 2

type LabelRules = ReachHudConfig['labels']

/**
 * Per-frame declutter scratch, built once. `order` is the label indices by priority; `show` and `opacity` are per
 * label index (LABEL_HIDDEN / LABEL_ICON / LABEL_FULL, 0..1); `rects` holds the occupied screen rectangles
 * (x0, y0, x1, y1), reserved markers first.
 */
export interface LabelDeclutter {
  readonly order: Int32Array
  readonly rank: Float64Array
  readonly show: Uint8Array
  readonly opacity: Float32Array
  readonly rects: Float32Array
  rectCount: number
}

/** Reserved rectangles beyond the labels themselves: the nav target and the route relay markers. */
const RESERVED_RECTS = 2
/** Kind ranks step by this, so any kind outranks the next whatever the distance (m). */
const KIND_STEP = 1e12

export function createLabelDeclutter(max: number): LabelDeclutter {
  return {
    order: new Int32Array(max),
    rank: new Float64Array(max),
    show: new Uint8Array(max),
    opacity: new Float32Array(max),
    rects: new Float32Array((max + RESERVED_RECTS) * 4),
    rectCount: 0,
  }
}

/** How many labels keep their text under a HUD labels setting. */
export function labelCap(mode: HudLabelMode, rules: LabelRules): number {
  return rules.caps[mode]
}

/** Clears the occupied rectangles; call once per frame before reserving markers and decluttering. */
export function resetLabelDeclutter(d: LabelDeclutter): void {
  d.rectCount = 0
}

/** Marks a screen rectangle as taken (a marker that outranks every label). Ignored when the scratch is full. */
export function reserveLabelRect(d: LabelDeclutter, x0: number, y0: number, x1: number, y1: number): void {
  const k = d.rectCount * 4
  if (k + 4 > d.rects.length) return
  d.rects[k] = x0
  d.rects[k + 1] = y0
  d.rects[k + 2] = x1
  d.rects[k + 3] = y1
  d.rectCount++
}

function overlapsAny(d: LabelDeclutter, x0: number, y0: number, x1: number, y1: number): boolean {
  const r = d.rects
  for (let i = 0; i < d.rectCount; i++) {
    const k = i * 4
    if (x0 < r[k + 2] && x1 > r[k] && y0 < r[k + 3] && y1 > r[k + 1]) return true
  }
  return false
}

function labelOpacity(distance: number, rules: LabelRules): number {
  const c = rules.declutter
  if (distance <= c.fadeStartM) return 1
  const span = rules.rangeM - c.fadeStartM
  const t = span > 0 ? (distance - c.fadeStartM) / span : 1
  return 1 - (1 - c.fadeMinOpacity) * (t > 1 ? 1 : t)
}

/**
 * Declutters the nearby labels by priority (H2): kind rank (stations first), then distance. While a target is
 * locked, labels within the reticle radius of the screen centre are hidden. The first `cap` labels whose plate
 * rectangle overlaps nothing taken keep their text; the rest drop to the icon. Each label fades with distance.
 * Allocation-free (D-014): the plate width is estimated from the text length, never measured.
 */
export function declutterLabels(
  d: LabelDeclutter,
  count: number,
  x: Float32Array,
  y: Float32Array,
  distance: Float64Array,
  kind: readonly string[],
  text: readonly string[],
  cap: number,
  centerX: number,
  centerY: number,
  locked: boolean,
  rules: LabelRules,
): void {
  const n = count < d.order.length ? count : d.order.length
  const ranks = rules.kindRank
  // Priority keys, then an insertion sort of the indices (n is a dozen at most).
  for (let i = 0; i < n; i++) {
    const k = kind[i]
    const kr = k === 'station' ? ranks.station : k === 'relay' ? ranks.relay : k === 'poi' ? ranks.poi : ranks.laneRing
    d.rank[i] = kr * KIND_STEP + distance[i]
    let j = i
    while (j > 0 && d.rank[d.order[j - 1]] > d.rank[i]) {
      d.order[j] = d.order[j - 1]
      j--
    }
    d.order[j] = i
  }
  const c = rules.declutter
  const r2 = c.reticleRadiusPx * c.reticleRadiusPx
  let full = 0
  for (let k = 0; k < n; k++) {
    const i = d.order[k]
    d.opacity[i] = labelOpacity(distance[i], rules)
    const dx = x[i] - centerX
    const dy = y[i] - centerY
    if (locked && dx * dx + dy * dy < r2) {
      d.show[i] = LABEL_HIDDEN
      continue
    }
    if (full >= cap) {
      d.show[i] = LABEL_ICON
      continue
    }
    const x0 = x[i] + c.offsetXPx
    const x1 = x0 + (text[i].length + c.distChars) * c.charPx + c.platePadPx
    const y0 = y[i] - c.plateHeightPx * 0.5
    const y1 = y0 + c.plateHeightPx
    if (overlapsAny(d, x0, y0, x1, y1)) {
      d.show[i] = LABEL_ICON
      continue
    }
    reserveLabelRect(d, x0, y0, x1, y1)
    d.show[i] = LABEL_FULL
    full++
  }
}
